import React, { useCallback, useEffect, useState } from 'react';
import { Clock, Loader2, Plus, X } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import type { WaitingListEntry } from '../types/clinical';
import type { Patient } from '../types/patient';

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'bg-red-100 text-red-800',
  'semi-urgent': 'bg-amber-100 text-amber-800',
  routine: 'bg-green-100 text-green-800',
};

const STATUS_COLORS: Record<string, string> = {
  waiting: 'bg-gray-100 text-gray-800',
  scheduled: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

export function WaitingListPage() {
  const { get, post, patch } = useApi();
  const [entries, setEntries] = useState<WaitingListEntry[]>([]);
  const [patientNames, setPatientNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);

  const [patientId, setPatientId] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [patientResults, setPatientResults] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [patientSearchLoading, setPatientSearchLoading] = useState(false);
  const [procedure, setProcedure] = useState('');
  const [priority, setPriority] = useState('routine');
  const [targetDate, setTargetDate] = useState('');
  const [notes, setNotes] = useState('');

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const result = await get<WaitingListEntry[]>('/waiting-list');
      if (result.success && result.data) {
        setEntries(result.data);
        const ids = Array.from(new Set(result.data.map(e => e.patientId)));
        const names: Record<string, string> = {};
        await Promise.all(ids.map(async (id) => {
          try {
            const p = await get<Patient>(`/patients/${id}`);
            if (p.success && p.data) names[id] = `${p.data.title ? p.data.title + ' ' : ''}${p.data.firstName} ${p.data.lastName}`;
          } catch { /* skip */ }
        }));
        setPatientNames(names);
      } else {
        setError(result.error || 'Failed to load waiting list');
      }
    } catch {
      setError('Failed to load waiting list');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // Patient typeahead search
  useEffect(() => {
    if (patientSearch.length < 2 || selectedPatient) { setPatientResults([]); return; }
    const timer = setTimeout(async () => {
      setPatientSearchLoading(true);
      try {
        const r = await get<Patient[]>(`/patients?search=${encodeURIComponent(patientSearch)}&activeOnly=true`);
        if (r.success && r.data) setPatientResults(r.data);
      } catch { /* ignore */ }
      setPatientSearchLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [patientSearch, selectedPatient, get]);

  const handleCreate = useCallback(async () => {
    const pid = selectedPatient?.id || patientId;
    if (!pid || !procedure) return;
    const result = await post('/waiting-list', { patientId: pid, procedure, priority, targetDate, notes });
    if (result.success) {
      setShowForm(false);
      setPatientId(''); setPatientSearch(''); setSelectedPatient(null); setProcedure(''); setPriority('routine'); setTargetDate(''); setNotes('');
      fetchEntries();
    } else {
      setError(result.error || 'Failed to add to waiting list');
    }
  }, [selectedPatient, patientId, procedure, priority, targetDate, notes, post, fetchEntries]);

  const handleUpdateStatus = useCallback(async (entryId: string, status: string) => {
    const result = await patch(`/waiting-list/${entryId}`, { status });
    if (result.success) {
      fetchEntries();
    } else {
      setError(result.error || 'Failed to update waiting list status');
    }
  }, [patch, fetchEntries]);

  return (
    <main className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Clock className="h-5 w-5 text-gray-400" /> Surgery Waiting List
        </h1>
        <button onClick={() => setShowForm(!showForm)} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-2">
          <Plus className="h-4 w-4" /> Add Entry
        </button>
      </div>

      {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {showForm && (
        <div className="mb-6 bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">Add to Waiting List</h3>
            <button onClick={() => setShowForm(false)}><X className="h-4 w-4 text-gray-400" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            {selectedPatient ? (
              <div className="flex items-center justify-between px-3 py-2 bg-blue-50 border border-blue-200 rounded text-sm">
                <span className="font-medium text-blue-900">{selectedPatient.title} {selectedPatient.firstName} {selectedPatient.lastName}</span>
                <button type="button" onClick={() => { setSelectedPatient(null); setPatientSearch(''); setPatientId(''); }} className="text-blue-600 hover:text-blue-800 text-xs">Change</button>
              </div>
            ) : (
              <div className="relative">
                <input value={patientSearch} onChange={e => setPatientSearch(e.target.value)} placeholder="Search patient..." className="w-full px-3 py-2 text-sm border rounded" autoFocus />
                {patientSearchLoading && <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-gray-400" />}
                {patientResults.length > 0 && (
                  <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-48 overflow-y-auto">
                    {patientResults.map(p => (
                      <li key={p.id}>
                        <button type="button" onClick={() => { setSelectedPatient(p); setPatientId(p.id); setPatientResults([]); setPatientSearch(''); }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm">
                          <span className="font-medium">{p.title} {p.firstName} {p.lastName}</span>
                          <span className="text-gray-500 ml-2">{p.idNumber}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <input value={procedure} onChange={e => setProcedure(e.target.value)} placeholder="Procedure" className="px-3 py-2 text-sm border rounded" />
            <select value={priority} onChange={e => setPriority(e.target.value)} className="px-3 py-2 text-sm border rounded">
              <option value="routine">Routine</option>
              <option value="semi-urgent">Semi-Urgent</option>
              <option value="urgent">Urgent</option>
            </select>
            <input type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)} className="px-3 py-2 text-sm border rounded" />
          </div>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes..." rows={2} className="w-full px-3 py-2 text-sm border rounded mb-3" />
          <button onClick={handleCreate} disabled={(!selectedPatient && !patientId) || !procedure} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">
            Add
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
      ) : entries.length === 0 ? (
        <p className="text-center py-8 text-gray-500">No patients on waiting list.</p>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Procedure</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {entries.map(entry => (
                <tr key={entry.id}>
                  <td className="px-4 py-3 text-sm text-gray-900">{patientNames[entry.patientId] || entry.patientId.slice(0, 8) + '...'}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{entry.procedure}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${PRIORITY_COLORS[entry.priority] || PRIORITY_COLORS.routine}`}>
                      {entry.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{entry.targetDate || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[entry.status] || STATUS_COLORS.waiting}`}>
                      {entry.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {entry.status === 'waiting' && (
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => handleUpdateStatus(entry.id, 'scheduled')} className="text-xs text-blue-600 hover:text-blue-800">Schedule</button>
                        <button onClick={() => handleUpdateStatus(entry.id, 'cancelled')} className="text-xs text-red-600 hover:text-red-800">Cancel</button>
                      </div>
                    )}
                    {entry.status === 'scheduled' && (
                      <button onClick={() => handleUpdateStatus(entry.id, 'completed')} className="text-xs text-green-600 hover:text-green-800">Complete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
